import { create } from "zustand";
import { persist } from "zustand/middleware";
import { browserJSONStorage } from "@/lib/persist-storage";
import type { CartItem } from "@/lib/cart";
import {
  addItem as addItemFn,
  removeItem as removeItemFn,
  updateQuantity as updateQuantityFn,
  CART_STORAGE_KEY,
} from "@/lib/cart";

interface CartState {
  items: CartItem[];
  _hasHydrated: boolean;
  /** Internal persist lifecycle signal used to settle hydration-dependent UI. */
  _setHasHydrated: (hydrated: boolean) => void;
  addItem: (item: Omit<CartItem, "quantity"> & { quantity?: number }) => void;
  /** Remove one cart line by its `cartLineKey` (productId + variant). */
  removeItem: (lineKey: string) => void;
  /** Bulk-remove every line for the given productIds (used by cart validation
   *  when a whole product is no longer orderable — drops all its variant lines). */
  removeItems: (productIds: string[]) => void;
  /** Update one cart line's quantity by its `cartLineKey`. */
  updateQuantity: (lineKey: string, quantity: number) => void;
  /**
   * Patch a subset of fields on existing cart items, keyed by productId.
   * Used by `useCartValidation` to refresh the `category` snapshot from
   * the server so a re-categorised product doesn't keep stale category
   * in the cart (which used to drive an inconsistent "Leeds-only"
   * delivery filter that the server then ignored).
   */
  patchItems: (
    updates: { productId: string; category?: string; deliveryTiming?: string }[]
  ) => void;
  clearCart: () => void;
}

export const useCart = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      _hasHydrated: false,
      _setHasHydrated: (hydrated) => set({ _hasHydrated: hydrated }),

      addItem: (item) =>
        set((state) => ({ items: addItemFn(state.items, item) })),

      removeItem: (lineKey) =>
        set((state) => ({ items: removeItemFn(state.items, lineKey) })),

      removeItems: (productIds) => {
        if (productIds.length === 0) return;
        const ids = new Set(productIds);
        set((state) => ({
          items: state.items.filter((i) => !ids.has(i.productId)),
        }));
      },

      updateQuantity: (lineKey, quantity) =>
        set((state) => ({
          items: updateQuantityFn(state.items, lineKey, quantity),
        })),

      patchItems: (updates) => {
        if (updates.length === 0) return;
        const map = new Map(updates.map((u) => [u.productId, u]));
        set((state) => ({
          items: state.items.map((it) => {
            const u = map.get(it.productId);
            if (!u) return it;
            const next = { ...it };
            if (u.category !== undefined) next.category = u.category;
            if (u.deliveryTiming !== undefined) next.deliveryTiming = u.deliveryTiming;
            return next;
          }),
        }));
      },

      clearCart: () => set({ items: [] }),
    }),
    {
      name: CART_STORAGE_KEY,
      // Explicit, and deliberately BYTE-IDENTICAL to the omitted default
      // (`createJSONStorage(() => window.localStorage)`): same key, same
      // `{"state":{"items":[…]},"version":0}` envelope. What it changes is the
      // SERVER, where the default throws and `persist` silently swaps in a `set`
      // that `console.warn`s on every call. See lib/persist-storage.ts.
      storage: browserJSONStorage("local"),
      // Only persist items, not actions
      partialize: (state) => ({ items: state.items }),
      /**
       * ⚠ VALIDATE THE PERSISTED SHAPE — this is the highest-blast-radius guard
       * in the storefront, not a nicety.
       *
       * The default `merge` splices the stored `items` in wholesale, so
       * `{"state":{"items":null},"version":0}` in `localStorage` — a truncated
       * write, a quota-exceeded partial flush, another tab, or a poke at
       * devtools — makes `useHydratedCartCount`'s `items.reduce(…)` throw. That
       * hook runs in `components/layout/Header.tsx`, which lives in the ROOT
       * LAYOUT, so a single bad blob blanks **every page on the site** for that
       * visitor, on every load, with no way out but clearing site data.
       *
       * `try/catch` cannot help: the JSON is perfectly valid, just the wrong
       * shape, so `persist` never sees an error. `migrate` cannot help either —
       * zustand only runs it when the stored `version` DIFFERS. `merge` runs on
       * every rehydrate, which is why the check belongs here.
       *
       * A dropped line is recoverable (the customer re-adds it); an unusable
       * site is not. `theme-store` already validates on every read for the same
       * reason.
       */
      merge: (persisted, current) => {
        const raw = (persisted as { items?: unknown } | undefined)?.items;
        const items = Array.isArray(raw)
          ? (raw.filter(
              (i) =>
                Boolean(i) &&
                typeof i === "object" &&
                typeof (i as CartItem).productId === "string" &&
                typeof (i as CartItem).quantity === "number" &&
                Number.isFinite((i as CartItem).quantity) &&
                typeof (i as CartItem).priceSnapshot === "number" &&
                Number.isFinite((i as CartItem).priceSnapshot)
            ) as CartItem[])
          : [];
        return { ...current, items };
      },
      // Capture the already-created state action instead of referencing
      // `useCart` from inside its own synchronous initialisation. The latter
      // hits the const temporal-dead-zone on a fresh browser load and leaves
      // checkout permanently displaying "Loading your cart…". Mark hydration
      // complete even when persisted JSON is corrupt so the empty-cart
      // fallback can recover instead of spinning forever.
      onRehydrateStorage: (state) => {
        // ⚠ SERVER PARITY, load-bearing. Giving `persist` a working SSR no-op
        // storage (above) means hydration now RUNS on the server, where it
        // previously could not. Without this guard `_hasHydrated` would flip to
        // true during SSR, and `CheckoutClient` gates its empty-cart UI on that
        // flag — the SSR HTML would swap from "redirecting/loading" to the
        // empty-cart CTA. Nothing is stored on the server, so there is nothing
        // to settle; keep the flag exactly where it was.
        if (typeof window === "undefined") return;
        // Zustand's synchronous JSON adapter skips its completion callback
        // when JSON.parse itself throws. This microtask is the error-path
        // fallback; for valid storage the returned callback settles first.
        queueMicrotask(() => state._setHasHydrated(true));
        return () => state._setHasHydrated(true);
      },
    }
  )
);
